import React, { useMemo, useState } from "react";

import { Dialog, Paragraph, Select, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiTreatmentPlanTemplateAiGenerationConfigChoices,
  ReportTemplateType,
  TreatmentPlanTemplateInput,
  useGetCurriculumsQuery,
  useUpdateReportTemplateMutation,
} from "@api/graphql/types-and-hooks";
import { ToggleSwitch } from "@components/ToggleSwitch/ToggleSwitch";
import { convertReadableString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface TreatmentPlanSettingsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  reportTemplate: ReportTemplateType;
  reportTemplateRefetch: () => Promise<void> | unknown;
}

const aiConfigOptions: SelectItem[] = Object.values(ApiTreatmentPlanTemplateAiGenerationConfigChoices).map((value) => ({
  id: value,
  primary: convertReadableString(value),
}));

export const TreatmentPlanSettingsDialog: React.FC<TreatmentPlanSettingsDialogProps> = ({
  open,
  setOpen,
  reportTemplate,
  reportTemplateRefetch,
}) => {
  const [aiGenerationConfig, setAiGenerationConfig] = useState<ApiTreatmentPlanTemplateAiGenerationConfigChoices>(
    (reportTemplate.treatmentPlanTemplate?.aiGenerationConfig as ApiTreatmentPlanTemplateAiGenerationConfigChoices) ??
      ApiTreatmentPlanTemplateAiGenerationConfigChoices.NoAiGeneration,
  );
  const [isGoalBankSuggestionsEnabled, setIsGoalBankSuggestionsEnabled] = useState<boolean>(
    reportTemplate.treatmentPlanTemplate?.isGoalBankSuggestionsEnabled ?? false,
  );
  const [isAddTargetsEnabled, setIsAddTargetsEnabled] = useState<boolean>(
    reportTemplate.treatmentPlanTemplate?.isAddTargetsEnabled ?? true,
  );
  const existingCurriculum = reportTemplate.curriculum;
  const [selectedCurriculum, setSelectedCurriculum] = useState<SelectItem | null>(
    existingCurriculum ? { id: existingCurriculum.id, primary: existingCurriculum.name } : null,
  );

  const { data: curriculaData } = useGetCurriculumsQuery({});
  const curricula = curriculaData?.getCurricula ?? [];
  const curriculumItems = useMemo(() => curricula.map((c) => ({ id: c.id, primary: c.name })), [curricula]);

  const updateReportTemplate = useUpdateReportTemplateMutation();

  const handleSave = async () => {
    const treatmentPlanTemplateInput: TreatmentPlanTemplateInput = {
      aiGenerationConfig,
      isGoalBankSuggestionsEnabled,
      isAddTargetsEnabled,
    };
    const input = {
      reportTemplateId: reportTemplate.id,
      treatmentPlanTemplate: treatmentPlanTemplateInput,
      goalBankCurriculumId: selectedCurriculum?.id ?? "",
    };

    await updateReportTemplate.mutateAsync(
      { input },
      {
        onSuccess: (response) => {
          const data = response.updateReportTemplate;
          if (!data?.status) {
            notifyError(data?.message || "Could not save treatment plan settings.");
            return;
          }
          notifySuccess(data.message || "Successfully saved treatment plan settings");
          reportTemplateRefetch();
          setOpen(false);
        },
        onError: (error: unknown) => {
          console.error(error);
          notifyError("Could not save treatment plan settings.");
        },
      },
    );
  };

  return (
    <Dialog
      title="Treatment Plan Settings"
      open={open}
      setOpen={setOpen}
      primaryButton={{
        text: "Save",
        onClick: handleSave,
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: () => setOpen(false),
      }}
      size="xl"
    >
      <div className="flex flex-col">
        <div>
          <Paragraph displayType="loud" colorType="primary" className="mb-2">
            AI Generation
          </Paragraph>
          <Select
            title=""
            items={aiConfigOptions}
            selected={aiConfigOptions.find((item) => item.id === aiGenerationConfig) ?? null}
            setSelected={(item) =>
              item && setAiGenerationConfig(item.id as ApiTreatmentPlanTemplateAiGenerationConfigChoices)
            }
            placeholderText="Select AI generation config"
          />
        </div>
        <div className="flex items-center justify-between pb-4">
          <Paragraph displayType="loud" colorType="primary">
            Goal Bank Suggestions Enabled
          </Paragraph>
          <ToggleSwitch enabled={isGoalBankSuggestionsEnabled} setEnabled={setIsGoalBankSuggestionsEnabled} />
        </div>
        <div className="flex items-center justify-between pb-4">
          <Paragraph displayType="loud" colorType="primary">
            Add Targets Enabled
          </Paragraph>
          <ToggleSwitch enabled={isAddTargetsEnabled} setEnabled={setIsAddTargetsEnabled} />
        </div>
        {curricula.length > 0 && (
          <div>
            <Paragraph displayType="loud" colorType="primary">
              Filter Goal Bank to Folder Category
            </Paragraph>
            <Select
              title=""
              placeholderText="All folders (Default)"
              items={curriculumItems}
              selected={selectedCurriculum}
              setSelected={setSelectedCurriculum}
              actionItem={
                selectedCurriculum
                  ? {
                      label: "Clear filter",
                      onClick: () => setSelectedCurriculum(null),
                      closeDropdown: true,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </Dialog>
  );
};
